import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { Magnetic } from "./scroll";
import { CONTACT, USPS } from "./marketingData";

const btnMotion = { whileHover: { scale: 1.04 }, whileTap: { scale: 0.97 } };

// Full festive greeting hero shown on festival days (replaces the standard hero).
export function FestivalHero({ festival }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const accent = festival.accent || "#C84B31";

  return (
    <section
      ref={ref}
      data-testid="festival-hero"
      data-festival={festival.key}
      className="relative min-h-[100vh] flex items-end pb-16 pt-28 overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={festival.image}
          alt={`${festival.name} greetings from Indian Transport Organisation`}
          className="w-full h-full object-cover"
        />
        {/* Left-weighted scrim so the greeting text stays readable */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F0E]/92 via-[#0F0F0E]/55 to-[#0F0F0E]/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0E]/80 to-transparent" />
        {/* Festive accent glow */}
        <div
          className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-30"
          style={{ backgroundColor: accent }}
        />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
        {/* Logo lockup */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <img src="/ito-logo.png" alt="ITO" className="w-12 h-12 object-contain drop-shadow" />
          <div className="leading-none">
            <div className="font-heading font-black tracking-tight text-white text-sm [text-shadow:0_1px_10px_rgba(0,0,0,0.5)]">
              INDIAN TRANSPORT
            </div>
            <div className="font-heading font-black tracking-tight text-[15px]" style={{ color: accent }}>
              ORGANISATION
            </div>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.25em] uppercase mb-5"
          style={{ color: accent }}
        >
          <Sparkles className="w-4 h-4" /> {festival.eyebrow}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.18 }}
          data-testid="festival-hero-title"
          className="font-heading font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-7xl max-w-4xl leading-[1.03] [text-shadow:0_2px_20px_rgba(0,0,0,0.45)]"
        >
          {festival.name}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
          data-testid="festival-hero-quote"
          className="mt-6 text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed [text-shadow:0_1px_10px_rgba(0,0,0,0.4)]"
        >
          {festival.quote}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-9 flex flex-wrap gap-4"
        >
          <Magnetic>
            <motion.a {...btnMotion} href={CONTACT.phoneHref} data-testid="hero-quote-btn" data-cursor
              className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-colors"
              style={{ backgroundColor: accent }}>
              <Phone className="w-4 h-4" /> Get a quote
            </motion.a>
          </Magnetic>
          <Magnetic>
            <motion.a {...btnMotion} href="#services" data-testid="hero-services-btn" data-cursor
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white hover:text-[#1A1A18] transition-colors">
              Explore services <ArrowRight className="w-4 h-4" />
            </motion.a>
          </Magnetic>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3"
        >
          {USPS.map((u, i) => (
            <span key={u} className="inline-flex items-center gap-2 text-sm font-semibold text-white/90">
              <CheckCircle2 className="w-4 h-4" style={{ color: accent }} /> {u}
            </span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
